import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  LayoutDashboard, Users, FileText, Calendar, 
  LogOut, Menu, X, ChevronRight, Shield, ArrowLeft, BarChart3,
  Megaphone, Newspaper, PenLine, Link2, Store, TreePine, ScrollText, ShieldCheck,
  CalendarDays, Play
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const navItems = [
  { icon: BarChart3, label: "Analytics", path: "/admin" },
  { icon: Users, label: "User Management", path: "/admin/users" },
  { icon: ScrollText, label: "Audit Log", path: "/admin/audit-log" },
  { icon: ShieldCheck, label: "2FA Security", path: "/admin/security" },
  { icon: FileText, label: "Content", path: "/admin/content" },
  { icon: Calendar, label: "Bookings", path: "/admin/bookings" },
  { icon: Megaphone, label: "Announcements", path: "/admin/announcements" },
  { icon: Newspaper, label: "ME.WELL Digest", path: "/admin/digest" },
  { icon: PenLine, label: "Blog Engine", path: "/admin/blog" },
  { icon: Link2, label: "Affiliates", path: "/admin/affiliates" },
  { icon: Store, label: "Wellness Mall", path: "/admin/mall" },
  { icon: TreePine, label: "Retreat Program", path: "/admin/retreat" },
  { icon: CalendarDays, label: "Calendar & Events", path: "/admin/calendar" },
  { icon: Play, label: "Media & Streaming", path: "/admin/media" },
];

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#0f1117] flex">
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 bg-[#161821] border-r border-white/5 transform transition-transform duration-300 ease-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full p-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <div className="leading-tight">
                <span className="text-white font-semibold text-sm block">Admin Portal</span>
                <span className="text-white/40 text-xs">SMAHOO ME.WELL</span>
                <span className="block text-[9px] font-normal text-amber-400/60 tracking-wide">Did you SMAHOO Today?</span>
              </div>
            </div>
            <button 
              className="lg:hidden p-2 hover:bg-white/5 rounded-xl"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>

          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl mb-6">
            <Avatar className="w-9 h-9">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-amber-500/20 text-amber-400 font-semibold text-sm">
                {user?.name?.charAt(0) || "A"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-white text-sm truncate">{user?.name || "Admin"}</p>
              <p className="text-xs text-amber-400/80 capitalize">{user?.role?.replace("_", " ") || "admin"}</p>
            </div>
          </div>

          <nav className="flex-1 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                    isActive 
                      ? 'bg-amber-500/10 text-amber-400' 
                      : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span>{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          <div className="border-t border-white/5 pt-4 mt-4 space-y-1">
            <Link 
              to="/dashboard"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/50 hover:text-white/80 hover:bg-white/5 transition-all"
              data-testid="admin-back-to-app"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              <span>Back to App</span>
            </Link>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm w-full text-red-400/70 hover:text-red-400 hover:bg-red-500/5 transition-all"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="lg:hidden sticky top-0 z-30 bg-[#0f1117]/90 backdrop-blur-xl border-b border-white/5 px-4 py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-white/5 rounded-xl"
              data-testid="admin-mobile-menu"
            >
              <Menu className="w-6 h-6 text-white/70" />
            </button>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-400" />
              <span className="text-white font-semibold text-sm">Admin Portal</span>
            </div>
            <Avatar className="w-8 h-8">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-amber-500/20 text-amber-400 text-sm">
                {user?.name?.charAt(0) || "A"}
              </AvatarFallback>
            </Avatar>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
